import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Loader2, Check, Sparkles, Search, AlertTriangle } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';
import { ModuleFeedback } from '../components/ModuleFeedback';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FRICCIONES = [
    { id: 'no_se_donde_empezar', label: 'No sé por dónde empezar', tipo: 'estructural' },
    { id: 'herramientas_no_listas', label: 'No tengo las herramientas listas', tipo: 'estructural' },
    { id: 'espacio_no_adecuado', label: 'Mi espacio no está adecuado', tipo: 'estructural' },
    { id: 'tarea_muy_grande', label: 'La tarea me parece demasiado grande', tipo: 'estructural' },
    { id: 'no_se_cuanto_tiempo', label: 'No sé cuánto tiempo me tomará', tipo: 'estructural' },
    { id: 'miedo_hacerlo_mal', label: 'Tengo miedo de hacerlo mal', tipo: 'emocional' },
    { id: 'me_siento_agotado', label: 'Me siento agotado/a antes de empezar', tipo: 'emocional' },
    { id: 'perfeccionismo', label: 'Si no va a quedar perfecto, prefiero no empezar', tipo: 'emocional' },
    { id: 'no_me_siento_listo', label: 'Siento que no estoy listo/a', tipo: 'emocional' },
    { id: 'autocritica', label: 'Me critico por no haberlo hecho antes', tipo: 'emocional' },
];

export default function Modulo51DetectorFriccion() {
    const [selected, setSelected] = useState([]);
    const [sintesis, setSintesis] = useState('');
    const [loading, setLoading] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [loadingDraft, setLoadingDraft] = useState(true);
    const [readOnly, setReadOnly] = useState(false);
    const [submittedAt, setSubmittedAt] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [aiInsight, setAiInsight] = useState('');
    const [diagnostico, setDiagnostico] = useState('');

    useEffect(() => {
        (async () => {
            try { const { data } = await axios.get(`${API_URL}/api/user/draft/detector_friccion`, { withCredentials: true }); if (data.data) { if (data.data.selected) setSelected(data.data.selected); if (data.data.sintesis) setSintesis(data.data.sintesis); } } catch {} finally { setLoadingDraft(false); }
        })();
    }, []);

    const saveDraft = useCallback(async () => { setSavingDraft(true); try { await axios.post(`${API_URL}/api/user/draft/save`, { module_id: 'detector_friccion', data: { selected, sintesis } }, { withCredentials: true }); } catch {} finally { setSavingDraft(false); } }, [selected, sintesis]);
    useEffect(() => { if (loadingDraft || readOnly) return; const t = setTimeout(saveDraft, 2000); return () => clearTimeout(t); }, [selected, sintesis, saveDraft, loadingDraft, readOnly]);

    const toggleFriccion = (id) => setSelected(prev => prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]);
    const canSubmit = !readOnly && (selected.length >= 2 && sintesis.trim().length > 0);

    const handleSubmit = async () => {
        if (readOnly) return;
        if (!canSubmit) return;
        setLoading(true);
        try {
            const { data } = await axios.post(`${API_URL}/api/user/modules/detector-friccion/submit`, {
                fricciones_seleccionadas: selected,
                sintesis: sintesis
            }, { withCredentials: true });
            setAiInsight(data.ai_insight); setDiagnostico(data.diagnostico); setSubmitted(true);
            toast.success('Análisis de fricción completado');
            if (data.read_only) { setReadOnly(true); setSubmittedAt(data.submitted_at); }
        } catch (error) { toast.error(error.response?.data?.detail || 'Error al enviar'); }
        finally { setLoading(false); }
    };

    if (loadingDraft) return <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" /></div>;

    if (submitted) return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col"><Toaster position="top-right" richColors />
            <div className="flex-1 flex items-center justify-center p-4">
                <Card className="w-full max-w-xl bg-white border border-[#DCE3E8] rounded-2xl shadow-xl"><CardContent className="pt-8 text-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-[#5FAF9D] to-[#4A9A89] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"><Search className="h-10 w-10 text-white" /></div>
                    <h2 className="text-2xl font-light text-[#1F2A30] font-['Outfit'] mb-2">Análisis completado</h2>
                    {diagnostico && <p className="text-[#3E5F6E] font-medium mb-4">{diagnostico}</p>}
                    {aiInsight && <div className="bg-[#F5F7F8] border-l-4 border-[#5FAF9D] p-4 rounded-r-xl text-left mb-6"><div className="flex items-start gap-3"><Sparkles className="h-5 w-5 text-[#5FAF9D] shrink-0 mt-0.5" /><p className="text-[#1F2A30] italic text-sm">"{aiInsight}"</p></div></div>}
                    <div className="mb-6"><ModuleFeedback moduleId="detector_friccion" chapter={5} /></div>
                    <Link to="/usuario"><Button className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-8 py-3 rounded-xl">Continuar</Button></Link>
                </CardContent></Card>
            </div><SENDAFooter /></div>
    );

    return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col"><Toaster position="top-right" richColors />
            <header className="bg-white/90 backdrop-blur-xl border-b border-[#DCE3E8]/50 sticky top-0 z-50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 sm:gap-4">
                            <Link to="/usuario"><Button variant="ghost" size="icon" className="text-[#3E5F6E]"><ArrowLeft className="h-5 w-5" /></Button></Link>
                            <div><div className="flex items-center gap-2"><span className="text-xs font-semibold text-[#E8B27A] uppercase tracking-wide">Cap. 5</span><span className="text-xs text-[#7A8A93]">5.1</span></div><h1 className="text-base sm:text-lg font-semibold text-[#1F2A30] font-['Outfit']">Detector de fricción</h1></div>
                        </div>
                        {savingDraft && <Loader2 className="h-3 w-3 animate-spin text-[#7A8A93]" />}
                    </div>
                </div>
            </header>
            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 flex-1">
                                {readOnly && <ReadOnlyBanner submittedAt={submittedAt} />}
                <Card className="bg-white border border-[#DCE3E8] rounded-2xl sm:rounded-3xl shadow-sm overflow-hidden">
                    <CardHeader className="bg-gradient-to-r from-[#E8B27A] to-[#D4A06A] p-4 sm:p-6 text-white">
                        <div className="flex items-center gap-3"><div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center"><Search className="h-5 w-5 sm:h-6 sm:w-6 text-white" /></div>
                            <div><CardTitle className="text-white text-base sm:text-lg">¿Qué te frena antes de empezar?</CardTitle><p className="text-xs sm:text-sm text-white/70">Selecciona al menos 2 fricciones que reconozcas</p></div></div>
                    </CardHeader>
                    <CardContent className="p-4 sm:p-6 space-y-5">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {FRICCIONES.map(f => (
                                <button key={f.id} onClick={() => toggleFriccion(f.id)}
                                    className={`p-3 rounded-xl text-left text-sm transition-all border ${
                                        selected.includes(f.id) ? 'bg-[#3E5F6E] text-white border-[#3E5F6E] shadow-md' : 'bg-white text-[#4A5B63] border-[#DCE3E8] hover:border-[#3E5F6E]/30'
                                    }`} data-testid={`friccion-${f.id}`}>
                                    <div className="flex items-center gap-2">
                                        {selected.includes(f.id) ? <Check className="h-4 w-4 shrink-0" /> : <div className="w-4 h-4 rounded border border-[#DCE3E8] shrink-0" />}
                                        <span>{f.label}</span>
                                    </div>
                                    <span className={`text-[10px] mt-1 block ${selected.includes(f.id) ? 'text-white/60' : 'text-[#9AA5B1]'}`}>{f.tipo}</span>
                                </button>
                            ))}
                        </div>
                        <div className="p-3 bg-[#E8B27A]/5 rounded-xl border border-[#E8B27A]/20">
                            <div className="flex items-center gap-2 mb-2"><AlertTriangle className="h-4 w-4 text-[#E8B27A]" /><span className="text-xs font-semibold text-[#E8B27A]">Seleccionadas: {selected.length}/10 (mínimo 2)</span></div>
                        </div>
                        <div className="space-y-2">
                            <Label className="text-sm font-semibold text-[#3E5F6E]">Síntesis: ¿qué patrón notas?</Label>
                            <Textarea value={sintesis} onChange={e => setSintesis(e.target.value)} placeholder="Describe brevemente qué patrón de fricción reconoces en ti..." className="border-[#DCE3E8] rounded-xl min-h-[80px]" data-testid="input-sintesis" />
                        </div>
                        <div className="flex justify-end pt-4 border-t border-[#DCE3E8]">
                            <Button onClick={handleSubmit} disabled={!canSubmit || loading} className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-6 sm:px-8 py-3 rounded-xl shadow-md" data-testid="submit-detector-friccion">
                                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Analizando...</> : <><Check className="h-4 w-4 mr-2" />Completar análisis</>}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </main><SENDAFooter />
        </div>
    );
}
